/**
 * Marketing pages · About / Services / Vehicles / Rates / Contact
 * =================================================================
 * Wrapped in a single file to keep the marketing-site scaffold tight.
 * All content is CMS-driven via /site/config.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Mail, Phone, MapPin, Car, ArrowRight, Send, Check } from "lucide-react";
import useSiteConfig from "@/hooks/useSiteConfig";
import RiderPricesPanel from "@/components/customer/RiderPricesPanel";
import PublicHoursBanner from "@/components/marketing/PublicHoursBanner";
import SmsConsent from "@/components/marketing/SmsConsent";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";

/**
 * v2.6.9 batch CG · PageHero now supports an optional `imageUrl` prop. When
 * provided, the hero renders ABOVE the top yellow line as a full-width
 * image strip and the title/subtitle band sits underneath it on its own
 * solid background. When omitted, falls back to the original solid hero.
 * Yellow line thinned from 4 px → 1 px per spec.
 */
function PageHero({ eyebrow, title, subtitle, imageUrl }) {
  return (
    <>
      {imageUrl && (
        <section
          className="w-full"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.32), rgba(0,0,0,0.55)), url("${imageUrl}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            minHeight: "220px",
          }}
          data-testid="page-hero-image"
        />
      )}
      <section className="bg-zinc-950 border-b border-amber-400 py-12 sm:py-16" data-testid="page-hero">
        <div className="max-w-7xl mx-auto px-5 text-center">
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold">{eyebrow}</div>
          <h1 className="text-4xl sm:text-5xl font-black text-zinc-100 mt-2">{title}</h1>
          {subtitle && <p className="text-zinc-400 mt-3 max-w-2xl mx-auto">{subtitle}</p>}
        </div>
      </section>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ABOUT
// ─────────────────────────────────────────────────────────────────────────
export function MarketingAbout() {
  const { cfg, loading } = useSiteConfig();
  if (loading || !cfg) return <MarketingLayout><div className="py-24 text-center"><Loader2 className="h-7 w-7 animate-spin mx-auto text-zinc-500"/></div></MarketingLayout>;
  const a = cfg.about;
  return (
    <MarketingLayout>
      <PageHero eyebrow="About Us" title={a.title} subtitle={a.subtitle} imageUrl={a.header_image_url}/>
      <section className="max-w-4xl mx-auto px-5 py-12 text-zinc-300 leading-relaxed text-base whitespace-pre-line" data-testid="about-body">
        {a.body}
      </section>
      {a.feature_images && a.feature_images.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 pb-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="about-images">
          {a.feature_images.filter(Boolean).map((url, i) => (
            <div key={`about-img-${url}-${i}`} className="rounded-xl overflow-hidden border border-zinc-800 aspect-video bg-zinc-900">
              <img src={url} alt="" className="w-full h-full object-cover"/>
            </div>
          ))}
        </section>
      )}
      <CTAStrip/>
    </MarketingLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// OUR SERVICES
// ─────────────────────────────────────────────────────────────────────────
export function MarketingServices() {
  const { cfg, loading } = useSiteConfig();
  if (loading || !cfg) return <MarketingLayout><div className="py-24 text-center"><Loader2 className="h-7 w-7 animate-spin mx-auto text-zinc-500"/></div></MarketingLayout>;
  const s = cfg.services;
  // v2.6.9 batch CG · Optional background image for the "What We Offer"
  // cards grid. If set, the grid sits over the image with a dark overlay
  // so the cards stay readable.
  const cardsBg = s.cards_bg_image_url;
  const cardsBgStyle = cardsBg
    ? {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.7)), url("${cardsBg}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;
  return (
    <MarketingLayout>
      <PageHero eyebrow="What We Offer" title={s.title} subtitle={s.intro} imageUrl={s.header_image_url}/>
      {/* v2.6.9 batch CG · Section renders with the admin-uploaded
          background image when present, otherwise falls back to the
          original white-card variant. Cards remain readable on both. */}
      <section
        className={cardsBg ? "" : "bg-white"}
        style={cardsBgStyle}
        data-testid="services-section"
      >
        <div className="max-w-7xl mx-auto px-5 py-14 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="services-grid">
          {s.services.map((svc, i) => (
            <div
              key={`svc-${svc.name || i}`}
              className={
                cardsBg
                  ? "bg-zinc-900/90 backdrop-blur-sm border-2 border-amber-400/40 hover:border-amber-400 transition-colors rounded-xl p-6 shadow-lg"
                  : "bg-zinc-50 border-2 border-zinc-200 hover:border-amber-400 transition-colors rounded-xl p-6 shadow-sm"
              }
              data-testid={`service-${i}`}
            >
              <div className={`text-[10px] uppercase tracking-widest font-bold ${cardsBg ? "text-amber-300" : "text-amber-600"}`}>Service · {i + 1}</div>
              <h3 className={`text-xl font-extrabold mt-1 ${cardsBg ? "text-zinc-100" : "text-zinc-900"}`}>{svc.name}</h3>
              <p className={`text-sm mt-2 leading-relaxed ${cardsBg ? "text-zinc-300" : "text-zinc-600"}`}>{svc.description}</p>
              <div className={`flex items-center justify-between mt-4 pt-4 border-t ${cardsBg ? "border-amber-400/20" : "border-zinc-200"}`}>
                <span className={`font-bold text-sm ${cardsBg ? "text-amber-300" : "text-amber-600"}`}>{svc.price}</span>
                <Link to="/ride/login"><Button size="sm" className="bg-zinc-950 hover:bg-zinc-800 text-amber-400 font-extrabold uppercase tracking-wider">Book <ArrowRight className="h-3.5 w-3.5 ml-1"/></Button></Link>
              </div>
            </div>
          ))}
        </div>
      </section>
      <CTAStrip/>
    </MarketingLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// OUR VEHICLES
// ─────────────────────────────────────────────────────────────────────────
export function MarketingVehicles() {
  const { cfg, loading } = useSiteConfig();
  if (loading || !cfg) return <MarketingLayout><div className="py-24 text-center"><Loader2 className="h-7 w-7 animate-spin mx-auto text-zinc-500"/></div></MarketingLayout>;
  const v = cfg.vehicles;
  return (
    <MarketingLayout>
      <PageHero eyebrow="The Fleet" title={v.title} subtitle={v.intro} imageUrl={v.header_image_url}/>
      <section className="max-w-7xl mx-auto px-5 py-12 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="vehicles-grid">
        {v.vehicles.map((veh, i) => (
          <Card key={`veh-${veh.name || i}`} className="bg-zinc-900 border-zinc-800 overflow-hidden p-0" data-testid={`vehicle-${i}`}>
            <div className="aspect-video bg-zinc-950 flex items-center justify-center border-b border-zinc-800">
              {veh.image_url ? (
                <img src={veh.image_url} alt={veh.name} className="w-full h-full object-cover"/>
              ) : (
                <Car className="h-24 w-24 text-amber-400/30"/>
              )}
            </div>
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">{veh.capacity}</div>
              <h3 className="text-xl font-extrabold text-zinc-100 mt-1">{veh.name}</h3>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{veh.description}</p>
            </div>
          </Card>
        ))}
      </section>
      <CTAStrip/>
    </MarketingLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TAXI RATES  — reuses the rider-facing RiderPricesPanel (CMS-driven)
// ─────────────────────────────────────────────────────────────────────────
export function MarketingRates() {
  const { cfg, loading } = useSiteConfig();
  const ratesHeader = cfg?.rates?.header_image_url;
  return (
    <MarketingLayout>
      <PageHero eyebrow="Transparent Pricing" title="Taxi Rates" subtitle="No hidden fees. Every fare is metered." imageUrl={ratesHeader}/>
      <section className="max-w-5xl mx-auto px-5 py-10" data-testid="rates-body">
        {/* The CMS-driven rider Prices panel doubles as the public rates page. */}
        <RiderPricesPanel onBookRide={() => { window.location.href = "/ride/login"; }}/>
      </section>
    </MarketingLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CONTACT US
// ─────────────────────────────────────────────────────────────────────────
export function MarketingContact() {
  const { cfg, loading } = useSiteConfig();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // v2.7.65+E165 · TCPA-style SMS consent — recorded server-side when the
  // rider provides a phone number. Optional (form only requires name/email/message).
  const [smsConsent, setSmsConsent] = useState(false);

  if (loading || !cfg) return <MarketingLayout><div className="py-24 text-center"><Loader2 className="h-7 w-7 animate-spin mx-auto text-zinc-500"/></div></MarketingLayout>;
  const c = cfg.contact;
  const g = cfg.global;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Name, email, and message are required");
    setSubmitting(true);
    try {
      await api.post("/site/contact", {
        ...form,
        sms_consent: !!smsConsent,
        sms_consent_ts: smsConsent ? new Date().toISOString() : null,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err, "Could not send message"));
    } finally { setSubmitting(false); }
  };

  return (
    <MarketingLayout>
      <PageHero eyebrow="Get In Touch" title={c.title} subtitle={c.intro} imageUrl={c.header_image_url}/>
      {/* v2.8+E302 · Prominent Hours of Operation section */}
      <PublicHoursBanner variant="card"/>
      <section className="max-w-6xl mx-auto px-5 py-12 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="contact-grid">
        <div className="space-y-3 md:col-span-1" data-testid="contact-info">
          <Card className="bg-zinc-900 border-zinc-800 p-5">
            <div className="flex items-center gap-3 mb-1.5">
              <div className="h-10 w-10 rounded-lg bg-amber-400/15 border border-amber-400/40 flex items-center justify-center text-amber-300"><Phone className="h-5 w-5"/></div>
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">{c.phone_label}</div>
            </div>
            <a href={`tel:${(g.phone || "").replace(/\D/g, "")}`} className="text-xl font-extrabold text-zinc-100 hover:text-amber-300">{g.phone}</a>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800 p-5">
            <div className="flex items-center gap-3 mb-1.5">
              <div className="h-10 w-10 rounded-lg bg-amber-400/15 border border-amber-400/40 flex items-center justify-center text-amber-300"><Mail className="h-5 w-5"/></div>
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">{c.email_label}</div>
            </div>
            <a href={`mailto:${g.email}`} className="text-base font-extrabold text-zinc-100 hover:text-amber-300 break-all">{g.email}</a>
          </Card>
          {g.address && (
            <Card className="bg-zinc-900 border-zinc-800 p-5">
              <div className="flex items-center gap-3 mb-1.5">
                <div className="h-10 w-10 rounded-lg bg-amber-400/15 border border-amber-400/40 flex items-center justify-center text-amber-300"><MapPin className="h-5 w-5"/></div>
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Service Area</div>
              </div>
              <div className="text-sm text-zinc-200 font-semibold">{g.address}</div>
            </Card>
          )}
        </div>

        <div className="md:col-span-2" data-testid="contact-form-col">
          {done ? (
            <Card className="bg-emerald-500/10 border-2 border-emerald-500/40 p-8 text-center" data-testid="contact-success">
              <div className="h-16 w-16 rounded-full bg-emerald-400 text-zinc-950 mx-auto flex items-center justify-center mb-3"><Check className="h-8 w-8"/></div>
              <h3 className="text-2xl font-extrabold text-zinc-100">{c.success_message}</h3>
              <Link to="/" className="inline-block mt-4 text-amber-300 hover:text-amber-200 underline text-sm font-semibold uppercase tracking-wider">← Return Home</Link>
            </Card>
          ) : (
            <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="contact-form">
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField label={c.form_labels.name} testid="contact-name">
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950 border-zinc-700 text-zinc-100" required/>
                  </FormField>
                  <FormField label={c.form_labels.email} testid="contact-email">
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-950 border-zinc-700 text-zinc-100" required/>
                  </FormField>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField label={c.form_labels.phone} testid="contact-phone">
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
                  </FormField>
                  <FormField label={c.form_labels.subject} testid="contact-subject">
                    <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
                  </FormField>
                </div>
                <FormField label={c.form_labels.message} testid="contact-message">
                  <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-zinc-950 border-zinc-700 text-zinc-100" required/>
                </FormField>
                {/* v2.7.65+E165 · TCPA-style SMS consent — only relevant when
                    the rider provides a phone number. Rendered regardless
                    so the disclosure is always visible above submit. */}
                <SmsConsent
                  value={smsConsent}
                  onChange={setSmsConsent}
                  testIdPrefix="contact-sms-consent"
                  className="pt-2"
                />
                <Button type="submit" disabled={submitting} className="w-full bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-wider" data-testid="contact-submit">
                  {submitting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/>Sending…</> : <><Send className="h-4 w-4 mr-1.5"/>{c.submit_button_label || "Send Message"}</>}
                </Button>
              </form>
            </Card>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}

function FormField({ label, testid, children }) {
  return (
    <div data-testid={`field-${testid}`}>
      <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function CTAStrip() {
  const { cfg } = useSiteConfig();
  // v2.6.9 batch CH · Gated by CMS toggle. Defaults OFF — strip hides on
  // every marketing page until an admin re-enables from Front End → Global.
  if (!cfg?.global?.show_ready_to_ride_cta) return null;
  const phone = cfg?.global?.phone || "(901) 459-9448";
  return (
    <section className="bg-amber-400 text-zinc-950 py-8 mt-6" data-testid="page-cta-strip">
      <div className="max-w-7xl mx-auto px-5 flex items-center justify-between gap-5 flex-wrap">
        <h2 className="text-xl sm:text-2xl font-black">Ready for your next ride?</h2>
        <div className="flex items-center gap-3">
          <a href={`tel:${phone.replace(/\D/g, "")}`}><Button className="bg-zinc-950 text-amber-300 hover:bg-zinc-900 font-extrabold uppercase"><Phone className="h-4 w-4 mr-1.5"/>{phone}</Button></a>
          <Link to="/ride/login"><Button className="bg-white text-zinc-950 hover:bg-zinc-100 font-extrabold uppercase">Book Online</Button></Link>
        </div>
      </div>
    </section>
  );
}
